import React, {Component, ReactElement, ReactNode} from 'react'

import NatRoomToResidentRepo from '../roomToResident/repository/NatRoomToResidentRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Messages} from '../../../Messages'
import {HKConstants} from '../../../HKConstants'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import PPFileInput from '../../../components/shared/forms/PPFileInput'
import {MESSAGE_TYPE, ROLE_FOR_RESIDENT} from '../../../CodeMaster'
import {NatPutResidentUserImport} from '../roomToResident/repository/NatPutResidentUserImport'
import {RequestItemErrors} from '../common/repository/NatBaseResponseInterface'
import {createRejectionResidentUserRegistrationApplyInfoMessage} from './NatAdminResidentUserHelper'
import {TransitionDestinationInfo} from './admin/NatTransitionDestinationInterface'
import {Location} from 'react-router-dom'

import {Table, TableBody, TableCell, TableRow} from '@mui/material'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import styled from 'styled-components'

const SESSION_STORAGE_KEY_SELECTED_CONDOMINIUM_CD: string = 'selectedCondominiumCd'

interface Props {
    RoomToResidentRepo: NatRoomToResidentRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    registerButtonDisabled: boolean
    registerImportButtonDisabled: boolean
    readFile: string | ArrayBuffer | null
    importErrorMessageText: Nullable<string>
}

class NatAdminResidentUserBulkUpdatePage extends Component<Props & AdminPagePropsSet, States> {
    selectedCondominiumCd: Nullable<string>
    returnLinkInfo: TransitionDestinationInfo

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.selectedCondominiumCd =
            SessionStorageHelper.getStringItemOrNull(SESSION_STORAGE_KEY_SELECTED_CONDOMINIUM_CD)
        this.returnLinkInfo = (this.props.location.state && this.props.location.state.returnLinkInfo)
            ? this.props.location.state.returnLinkInfo
            : {label: '居住者ユーザ検索へ戻る', url: '/admin/residentUserList'}

        this.state = {
            registerButtonDisabled: false,
            registerImportButtonDisabled: true,
            readFile: '',
            importErrorMessageText: null,
        }

        this.onChangeFile = this.onChangeFile.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.registerResidentUser = this.registerResidentUser.bind(this)
        this.handleEmitMessage = this.handleEmitMessage.bind(this)
    }

    // 登録ボタン押下
    async registerResidentUser(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0002.errorLevel,
                message: Messages.IOPCC0002.toString('居住者ユーザ'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleRegisterOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {
            let fileData: string = ''
            if (this.state.readFile) {
                const readData: string[] = String(this.state.readFile).split(',')
                fileData = readData[1]
            }

            const putRoomToResidentImportResult:
                Nullable<NatPutResidentUserImport> = await this.props.asyncCallFunction(
                this.props.RoomToResidentRepo.putRoomToResidentImport.bind(
                    this.props.RoomToResidentRepo, this.selectedCondominiumCd as string, fileData),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('居住者ユーザ')},
            )

            if (putRoomToResidentImportResult &&
                putRoomToResidentImportResult.requestItemErrors === null &&
                putRoomToResidentImportResult.requestError === null) {
                const adminNameUpdateExclusionMessage: string[] = []
                if (putRoomToResidentImportResult.details &&
                    putRoomToResidentImportResult.details.adminNameUpdateExclusionList) {
                    putRoomToResidentImportResult.details.adminNameUpdateExclusionList.map(
                        (adminNameUpdateExclusion) => {
                            const message: string = this.createWarningMessage(
                                adminNameUpdateExclusion.ridgeName,
                                adminNameUpdateExclusion.ridgeCd,
                                adminNameUpdateExclusion.roomNumber,
                                adminNameUpdateExclusion.roleId,
                            )
                            adminNameUpdateExclusionMessage.push(message)
                        })
                }

                const validPeriodEndUpdateExclusionMessage: string[] = []
                if (putRoomToResidentImportResult.details &&
                    putRoomToResidentImportResult.details.validPeriodEndUpdateExclusionList) {
                    putRoomToResidentImportResult.details.validPeriodEndUpdateExclusionList.map(
                        (validPeriodEndUpdateExclusion) => {
                            const message: string = this.createWarningMessage(
                                validPeriodEndUpdateExclusion.ridgeName,
                                validPeriodEndUpdateExclusion.ridgeCd,
                                validPeriodEndUpdateExclusion.roomNumber,
                                validPeriodEndUpdateExclusion.roleId,
                            )
                            validPeriodEndUpdateExclusionMessage.push(message)
                        })
                }

                const validPeriodDateUpdateExclusionForFacilityReserveMessage: string[] = []
                if (putRoomToResidentImportResult.details &&
                    putRoomToResidentImportResult.details.validPeriodDateUpdateExclusionListForFacilityReserve) {
                    putRoomToResidentImportResult.details.validPeriodDateUpdateExclusionListForFacilityReserve.map(
                        (validPeriodDateUpdateExclusion) => {
                            const message: string = this.createWarningMessage(
                                validPeriodDateUpdateExclusion.ridgeName,
                                validPeriodDateUpdateExclusion.ridgeCd,
                                validPeriodDateUpdateExclusion.roomNumber,
                                validPeriodDateUpdateExclusion.roleId,
                            )
                            validPeriodDateUpdateExclusionForFacilityReserveMessage.push(message)
                        })
                }

                let rejectionResidentUserRegistrationApplyInfoListMessage = ''
                if (putRoomToResidentImportResult.details &&
                    putRoomToResidentImportResult.details.rejectionRegistrationApplyInfoList.length) {
                    rejectionResidentUserRegistrationApplyInfoListMessage =
                        createRejectionResidentUserRegistrationApplyInfoMessage(
                            putRoomToResidentImportResult.details.rejectionRegistrationApplyInfoList)
                }

                let detailMessage: string = Messages.IOPCC0008.toString('居住者ユーザ')
                const addDetailMessage: string[] = []
                if (adminNameUpdateExclusionMessage.length !== 0) {
                    addDetailMessage.push(
                        '\n'
                        + Messages.IOPCC0022.toString(adminNameUpdateExclusionMessage.join('\n')),
                    )
                }
                if (validPeriodEndUpdateExclusionMessage.length !== 0) {
                    addDetailMessage.push(
                        '\n'
                        + Messages.IOPCC0023.toString(validPeriodEndUpdateExclusionMessage.join('\n')),
                    )
                }
                if (validPeriodDateUpdateExclusionForFacilityReserveMessage.length !== 0) {
                    addDetailMessage.push(
                        '\n'
                        + Messages.IOPCC0024.toString(
                            validPeriodDateUpdateExclusionForFacilityReserveMessage.join('\n'),
                        ),
                    )
                }
                if (rejectionResidentUserRegistrationApplyInfoListMessage) {
                    addDetailMessage.push(
                        '\n'
                        + rejectionResidentUserRegistrationApplyInfoListMessage)
                }
                detailMessage = detailMessage + addDetailMessage.join('\n')
                this.props.navigate(`/admin/residentUserList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: detailMessage,
                            },
                        },
                    },
                )
            } else {
                this.props.eventEmitter.on('invalidFile', this.handleEmitMessage)
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    createWarningMessage(ridgeName: string, ridgeCd: string, roomNumber: string, roleId: string): string {
        return ridgeName
            + '(棟コード:' + ridgeCd + ')'
            + ' ' + roomNumber
            + ' ' + ROLE_FOR_RESIDENT.codeToEnumConstants(roleId).label
    }

    handleEmitMessage(itemError: RequestItemErrors): void {
        this.setState({
            registerImportButtonDisabled: true,
            importErrorMessageText: itemError.message,
        })
    }

    onChangeFile(event: React.ChangeEvent<HTMLInputElement>): void {
        // ファイル情報取得
        if (event.target.files && event.target.files[0]) {
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                this.setState({
                    readFile: reader.result,
                    registerImportButtonDisabled: false,
                    importErrorMessageText: null,
                })
            }
            reader.readAsDataURL(event.target.files[0])
        } else {
            this.setState({
                registerImportButtonDisabled: true,
                importErrorMessageText: null,
            })
        }
    }

    renderInputFormTable(): ReactNode {
        const countLimit = formatNumberWithCommas(HKConstants.VMN.RES.RESIDENCE_USER_IMPORT_CSV_LINE_COUNT_LIMIT)

        return (
            <>
                <Table>
                    <StyledTableCaption>居住者ユーザ情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>居住者ユーザ一括更新CSV</TableRowName>
                            <TableCell>
                                <FileInputArea>
                                    <PPFileInput
                                        InputProps={{
                                            inputProps: {
                                                id: 'residentUserCsv',
                                                name: 'residentUserCsv',
                                            },
                                        }}
                                        fullWidth={false}
                                        onChange={this.onChangeFile}
                                        eventKeys={['residentUserBulkUpdationData']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                    1ファイル5MB、 データ件数{countLimit}件まで
                                </FileInputArea>
                            </TableCell>
                        </TableRow>
                        {(this.state.importErrorMessageText) ?
                            <TableRow>
                                <TableRowName>エラー内容</TableRowName>
                                <TableCell>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                readOnly: true,
                                                minRows: 5,
                                                maxRows: 5,
                                            },
                                        }}
                                        fullWidth
                                        multiline
                                        value={this.state.importErrorMessageText}
                                    />
                                </TableCell>
                            </TableRow> : <></>
                        }
                    </TableBody>
                </Table>
            </>
        )
    }


    renderPageFooterArea(): ReactNode {
        return (
            <>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        disabled={this.state.registerImportButtonDisabled}
                        onClick={this.registerResidentUser}
                    >
                        登録する
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
                <PageFotterAreaLeft>
                    <BackLink onClick={() => this.props.navigate(this.returnLinkInfo.url)}>
                        {this.returnLinkInfo.label}
                    </BackLink>
                </PageFotterAreaLeft>
            </>
        )
    }

    render(): ReactElement {
        return (
            <>
                <PageMainArea>
                    <form>
                        {this.renderInputFormTable()}
                    </form>
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminResidentUserBulkUpdatePage)

const FileInputArea = styled(LeftRowFlexContainer)`
  gap: 16px;
`
